import {
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  from,
  fromPromise,
  split
} from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { getMainDefinition } from '@apollo/client/utilities'
import { GetTokenSilentlyOptions, useAuth0 } from '@auth0/auth0-react'
import { SentryLink } from 'apollo-link-sentry'
import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink'
import React, { createContext, FC, useContext, useEffect, useState } from 'react'

import { useNotification } from '@/contexts'
import {
  ContextServiceCurrentUpdateDocument,
  ContextServiceCurrentUpdateSubscription,
  ContextServiceDocument,
  ContextServiceQuery
} from '@/graphql/schema'
import { initializeApollo } from '@/utils/apollo'

import { ServiceContextType } from './types'

const getAccessToken = async (
  getAccessToken: (options?: GetTokenSilentlyOptions) => Promise<string>
) => {
  const domain = process.env.NEXT_PUBLIC_AUTH0_DOMAIN
  const accessToken = await getAccessToken({
    audience: `https://${domain}/api/v2/`,
    scope: 'read:current_user'
  })

  return accessToken
}

const ServiceContext = createContext<ServiceContextType>({
  loadedCurrent: false,
  connectedToBackend: false,
  connectedToAuth: false
})

const ServiceProvider: FC = ({ children }) => {
  const { loginWithRedirect, getAccessTokenSilently, user, isAuthenticated, isLoading } = useAuth0()

  // Poster上の情報
  const [context, setContext] = useState<ServiceContextType>({
    loadedCurrent: false,
    connectedToBackend: false,
    connectedToAuth: false
  })

  // 通知
  const { setNotify } = useNotification()

  // APIと疎通
  useEffect(() => {
    const generateApolloClient = async () => {
      if (isAuthenticated && typeof window !== 'undefined') {
        // actioncableパッケージはSSRに対応しないため、クライアントの判定ができたタイミングで読み込む処理を行う
        // ref1 https://github.com/rails/rails/pull/39543
        // ref2 https://github.com/rails/rails/pull/37261
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const ActionCable = require('actioncable')
        const httpLink = createHttpLink({
          uri: `${process.env.NEXT_PUBLIC_API_URL}/graphql`
        })
        const cable = ActionCable.createConsumer(
          `${process.env.NEXT_PUBLIC_WS_URL}/cable?idToken=${await getAccessToken(
            getAccessTokenSilently
          )}`
        )

        const connectionLink = split(
          ({ query }) => {
            const definition = getMainDefinition(query)
            return (
              definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
            )
          },
          new ActionCableLink({
            cable,
            channelName: 'ApplicationCable::GraphqlChannel'
          }),
          httpLink
        )

        const authLink = new ApolloLink((operation, forward) => {
          return fromPromise(getAccessToken(getAccessTokenSilently)).flatMap((accessToken) => {
            operation.setContext(({ headers }) => {
              return {
                headers: {
                  ...headers,
                  authorization: `Bearer ${accessToken}`
                }
              }
            })
            return forward(operation)
          })
        })

        const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
          // バックエンド側で発生したリトライすべきエラーはリトライする
          if (graphQLErrors) {
            for (const error of graphQLErrors) {
              switch (error.extensions?.code) {
                case 'AUTH_ERROR':
                  setNotify(error.message, 'warning')
                  return fromPromise(getAccessToken(getAccessTokenSilently)).flatMap(
                    (accessToken) => {
                      operation.setContext(({ headers }) => {
                        return {
                          headers: {
                            ...headers,
                            authorization: `Bearer ${accessToken}`
                          }
                        }
                      })
                      return forward(operation)
                    }
                  )
                case 'USER_ERROR':
                  setNotify(error.message, 'warning')
                  break
                default:
                  setNotify(error.extensions?.code_message as string, 'error')
                  break
              }
            }
          }

          if (networkError) {
            setNotify(networkError.message, 'error')
          }
        })

        const sentryLink = new SentryLink()

        setContext({
          ...context,
          apolloClient: initializeApollo(from([sentryLink, authLink, errorLink, connectionLink])),
          connectedToBackend: true
        })
      }
    }

    generateApolloClient()
  }, [isAuthenticated])

  useEffect(() => {
    if (isAuthenticated || isLoading) {
      return
    }

    const getSession = async () => {
      await loginWithRedirect({ appState: { returnTo: window?.location?.pathname } })
    }
    getSession()
  }, [isAuthenticated, isLoading])

  useEffect(() => {
    if (context.connectedToBackend) {
      // queryを試してログインが可能かどうかを確認する
      const getCurrent = async () => {
        const { data } = await context.apolloClient
          .query<ContextServiceQuery>({
            query: ContextServiceDocument
          })
          .finally(() => setContext({ ...context, loadedCurrent: true }))

        setContext({ ...context, current: data.current })
      }

      // subscription
      const subsctibeCurrentUpdate = async () => {
        await context.apolloClient
          .subscribe<ContextServiceCurrentUpdateSubscription>({
            query: ContextServiceCurrentUpdateDocument
          })
          .subscribe(({ data }) => {
            if (data.currentUpdate) {
              setContext({ ...context, current: data.currentUpdate.current })
            }
          })
      }

      getCurrent()
      subsctibeCurrentUpdate()
    }
  }, [context.connectedToBackend])

  return context.connectedToBackend ? (
    <ServiceContext.Provider value={{ ...context, connectedToAuth: !isLoading, authUser: user }}>
      <ApolloProvider client={context.apolloClient}>{children}</ApolloProvider>
    </ServiceContext.Provider>
  ) : (
    <ServiceContext.Provider value={{ ...context, connectedToAuth: !isLoading, authUser: user }}>
      {children}
    </ServiceContext.Provider>
  )
}

const useService = (): ServiceContextType => useContext(ServiceContext)

export { ServiceContext, ServiceProvider, useService }
